import fr from 'date-fns/locale/fr/index.js';
import enGB from 'date-fns/locale/en-GB/index.js';
import {
	differenceInMonths,
	differenceInYears,
	format,
	formatDistanceToNow,
	intervalToDuration,
	isAfter,
	isBefore,
	isValid,
	subYears,
	getYear,
	parseISO,
	isEqual
} from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import { locale as i18nLocale } from 'svelte-i18n';
import { get } from 'svelte/store';
import { capitalize } from './strings';

export type DateType = Date | string;

export const parseApiDate = (date: DateType): Date => {
	let parsedDate: Date;

	if (typeof date == 'string') {
		if (date.endsWith('Z') || date.indexOf('+') > 0) {
			// the date has a timezone, trust the ISO format
			parsedDate = parseISO(date);
		} else {
			// if the string doesn't contain any timezone information, force it to be Paris
			parsedDate = zonedTimeToUtc(date, 'Europe/Paris');
		}
	} else {
		parsedDate = date;
	}

	return parsedDate;
};

export const getDateFnsLocale = (locale: string) => {
	let dateFnsLocale;

	if (locale.startsWith('fr')) {
		dateFnsLocale = fr;
	} else {
		dateFnsLocale = enGB;
	}

	return dateFnsLocale;
};

export const formatDateWithLocale = (date: DateType, pattern?: string): string => {
	const locale = get(i18nLocale);
	const parsedDate = parseApiDate(date);
	let localPattern = pattern;
	if (!localPattern) {
		if (locale?.startsWith('en')) {
			localPattern = 'yyyy/MM/dd';
		} else {
			localPattern = 'dd/MM/yyyy';
		}
	}
	return format(parsedDate, localPattern, {
		locale: getDateFnsLocale(locale ?? 'fr')
	});
};

export const formatDateToWSAPI = (date: Date): string => {
	return format(date, 'yyyy-MM-dd', {
		locale: fr as Locale
	});
};

export const formatDateTimeWithLocale = (date: DateType): string => {
	return format(parseApiDate(date), 'ccc PPPp', {
		locale: getDateFnsLocale('fr')
	});
};

export const formatDateTimeWithLocaleMinimize = (date: DateType): string => {
	return format(parseApiDate(date), 'ccc PPp', {
		locale: getDateFnsLocale('fr')
	});
};

export const formatDateDistance = (date: DateType, options?: Record<string, never>): string => {
	try {
		return formatDistanceToNow(parseApiDate(date), {
			addSuffix: true,
			locale: getDateFnsLocale('fr'),
			...options
		});
	} catch (e) {
		return '';
	}
};

export const getMonthsBetween = (a: DateType, b: DateType): number => {
	return differenceInMonths(parseApiDate(a), parseApiDate(b));
};

export const getYearsBetween = (a: DateType, b: DateType): number => {
	return differenceInYears(parseApiDate(a), parseApiDate(b));
};

export const isDateAfter = (a: DateType, b?: DateType): boolean => {
	if (b) {
		return isAfter(parseApiDate(a), parseApiDate(b));
	}
	return isAfter(parseApiDate(a), new Date());
};

export const isDateBefore = (a: DateType, b?: DateType): boolean => {
	if (b) {
		return isBefore(parseApiDate(a), parseApiDate(b));
	}
	return isBefore(parseApiDate(a), new Date());
};

export const isDateEqual = (a: DateType, b?: DateType): boolean => {
	if (b) {
		return isEqual(parseApiDate(a), parseApiDate(b));
	}
	const d = new Date();
	d.setSeconds(0, 0);
	return isEqual(parseApiDate(a), d);
};

export const isDateBetween = (date: DateType, startDate: DateType, endDate: DateType) => {
	return isDateAfter(date, startDate) && isDateBefore(date, endDate);
};

export const parseDateISO = (s: string): Date => {
	return parseISO(s);
};

export const remainingTime = (endDate: DateType, startDate: DateType = new Date()) => {
	return intervalToDuration({
		start: parseApiDate(startDate),
		end: parseApiDate(endDate)
	});
};

export const isDateValid = (value: unknown): boolean => {
	return isValid(value);
};

export const getEstimatedDateText = (date: DateType): string => {
	const localPattern = 'MMMM yyyy';
	const locale = get(i18nLocale);
	const d = parseApiDate(date);
	const res = d.getDate() < 15 ? 'début ' : 'fin ';
	const formatedDate = format(d, localPattern, {
		locale: getDateFnsLocale(locale ?? 'fr')
	});
	return formatedDate ? res + capitalize(formatedDate) : '';
};

export const getSubyears = (date: DateType, i: number): Date => {
	return subYears(parseApiDate(date), i);
};

export const getYearFromDate = (date: DateType): number => {
	return getYear(parseApiDate(date));
};
